import React, { useState } from 'react';
import { formatCurrency, formatNumber } from '../utils/formatters';
import './ValuationDisplay.css';

/**
 * ValuationDisplay - Scalable component for displaying ARV analysis
 * 
 * Supports structured valuation data with:
 * - Confidence badge (high/medium/low)
 * - Comparables table with flip indicators
 * - Adjustments breakdown
 * - Renovation cost breakdown
 * - Summary text
 * 
 * Falls back gracefully when data is missing (backwards compatible)
 */
const ValuationDisplay = ({ arv, valuationDetails, fallbackExplanation, subjectDetails }) => {
    // Hooks must be called before any conditional returns
    const [showCalculation, setShowCalculation] = useState(false);
    const [showRenovation, setShowRenovation] = useState(true);  // Expanded by default
    const [showAdjustments, setShowAdjustments] = useState(true);  // Expanded by default
    const [showComps, setShowComps] = useState(true);  // Expanded by default

    // If no structured data, show fallback
    if (!valuationDetails || Object.keys(valuationDetails).length === 0) {
        return (
            <div className="vd-summary">
                <p className="vd-summary-text">
                    {fallbackExplanation || 'No detailed analysis available.'}
                </p>
            </div>
        );
    }

    // Fallback to full_response for backwards compatibility with existing data
    const fullResponse = valuationDetails.full_response || {};

    const confidence = valuationDetails.confidence || fullResponse.confidence;
    const comparables = valuationDetails.comparables || fullResponse.comparables || [];
    const adjustments = valuationDetails.adjustments || fullResponse.adjustments || [];
    const renovation_breakdown = valuationDetails.renovation_breakdown || fullResponse.renovation_breakdown;
    const avg_adjusted_ppsf = valuationDetails.avg_adjusted_ppsf || fullResponse.avg_adjusted_ppsf;
    const arv_calculation = valuationDetails.arv_calculation || fullResponse.arv_calculation;
    const valuation_summary = valuationDetails.valuation_summary || fullResponse.valuation_summary;
    const deal_summary = valuationDetails.deal_summary || fullResponse.deal_summary;

    // Helper to format date
    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    return (
        <div className="valuation-display">
            {/* AI Verdict Header */}
            <div className="vd-verdict-header">
                <span className="vd-robot-icon">🤖</span>
                <span className="vd-verdict-title">AI Verdict</span>
            </div>

            {/* ARV & Confidence Row */}
            <div className="vd-price-row">
                <span className="vd-arv-value">{formatCurrency(arv)}</span>
                {confidence && (
                    <span className={`vd-confidence-badge ${confidence}`}>
                        {confidence === 'high' && '✓ '}
                        {confidence === 'low' && '⚠ '}
                        {confidence} CONFIDENCE
                    </span>
                )}
            </div>

            {/* Deal Assessment (Purple Box) */}
            {deal_summary && (
                <div className="vd-deal-assessment">
                    <span className="vd-assessment-icon">📊</span>
                    <span className="vd-assessment-text">
                        <strong>Deal Assessment:</strong> {deal_summary}
                    </span>
                </div>
            )}

            {/* 3-Card Quick Metrics */}
            <div className="vd-quick-cards">
                <div className="vd-quick-card">
                    <span className="vd-card-label">AVG $/SQFT</span>
                    <span className="vd-card-value">{avg_adjusted_ppsf ? `$${avg_adjusted_ppsf.toFixed(0)}` : '—'}</span>
                </div>
                <div className="vd-quick-card">
                    <span className="vd-card-label">EST. RENOVATION</span>
                    <span className="vd-card-value">{formatCurrency(renovation_breakdown?.total || 0)}</span>
                </div>
                <div className="vd-quick-card">
                    <span className="vd-card-label">COMPS USED</span>
                    <span className="vd-card-value">{comparables?.length || 0}</span>
                </div>
            </div>

            {/* Comparables Section - Collapsible */}
            {comparables && comparables.length > 0 && (
                <div className="vd-section">
                    <div
                        className="vd-section-header vd-toggle-header"
                        onClick={() => setShowComps(!showComps)}
                    >
                        <span>
                            <span className="vd-section-icon">📍</span>
                            Comparable Properties ({comparables.length})
                        </span>
                        <span className={`vd-toggle-arrow ${showComps ? 'open' : ''}`}>▼</span>
                    </div>

                    {showComps && (
                        <>

                            <table className="vd-comps-table">
                                <thead>
                                    {/* Subject Property Row */}
                                    {subjectDetails && (
                                        <tr className="vd-subject-row">
                                            <td>
                                                <span className="vd-subject-label">SUBJECT:</span>
                                                <span className="vd-subject-details">
                                                    {subjectDetails.beds} Beds · {formatNumber(subjectDetails.sqft)} Sqft · Built {subjectDetails.year_built} · {subjectDetails.baths} Bath
                                                </span>
                                            </td>
                                            <td colSpan="7"></td>
                                        </tr>
                                    )}
                                    <tr>
                                        <th>ADDRESS</th>
                                        <th>BEDS</th>
                                        <th>SQFT</th>
                                        <th>SOLD</th>
                                        <th>$/SQFT</th>
                                        <th>DIST</th>
                                        <th>DATE</th>
                                        <th>TIER</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {comparables.map((comp, idx) => (
                                        <tr key={idx}>
                                            <td>
                                                <div className="vd-comp-address-wrapper">
                                                    <span className="vd-comp-address" title={comp.address}>
                                                        {comp.address}
                                                    </span>
                                                    {comp.is_flip && (
                                                        <span className="vd-flip-tag">FLIP</span>
                                                    )}
                                                    {comp.selection_reason && (
                                                        <span className="vd-info-icon" title={comp.selection_reason}>ℹ️</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>{comp.beds || '—'}</td>
                                            <td>{comp.sqft ? formatNumber(comp.sqft) : '—'}</td>
                                            <td>{comp.sale_price ? formatCurrency(comp.sale_price) : '—'}</td>
                                            <td>${comp.price_per_sqft?.toFixed(0) || '—'}</td>
                                            <td>{comp.distance_miles?.toFixed(1) || '—'}mi</td>
                                            <td>{formatDate(comp.sale_date)}</td>
                                            <td>
                                                <span className={`vd-tier-badge tier-${comp.tier || 1}`}>
                                                    {comp.tier || 1}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {/* Valuation Summary - Right after comparables */}
                            {valuation_summary && (
                                <div className="vd-summary-inline">
                                    <p className="vd-summary-text-inline">💡 {valuation_summary}</p>
                                </div>
                            )}

                            {/* ARV Calculation Summary */}
                            {(avg_adjusted_ppsf || arv_calculation) && (
                                <div className="vd-calculation-section">
                                    <div className="vd-calculation-header">
                                        <div className="vd-avg-ppsf">
                                            <span className="vd-avg-label">Avg Adjusted $/sqft:</span>
                                            <span className="vd-avg-value">${avg_adjusted_ppsf?.toFixed(0) || '—'}</span>
                                        </div>
                                        {arv_calculation && (
                                            <button
                                                className="vd-show-calc-btn"
                                                onClick={() => setShowCalculation(!showCalculation)}
                                            >
                                                {showCalculation ? 'Hide' : 'Show'} calculation
                                            </button>
                                        )}
                                    </div>
                                    {showCalculation && arv_calculation && (
                                        <div className="vd-calculation-detail">
                                            <div className="vd-calc-formula">
                                                <div className="vd-calc-title">📐 ARV Calculation Method</div>
                                                <div className="vd-calc-steps">
                                                    <div className="vd-calc-step">
                                                        <span className="vd-calc-step-label">1. Weighted Average $/sqft</span>
                                                        <span className="vd-calc-step-value">${avg_adjusted_ppsf?.toFixed(0) || '—'}</span>
                                                    </div>
                                                    <div className="vd-calc-step">
                                                        <span className="vd-calc-step-label">2. Subject Sqft</span>
                                                        <span className="vd-calc-step-value">{formatNumber(Math.round(arv / (avg_adjusted_ppsf || 1)))} sqft</span>
                                                    </div>
                                                    <div className="vd-calc-step result">
                                                        <span className="vd-calc-step-label">3. ARV = Sqft × $/sqft</span>
                                                        <span className="vd-calc-step-value">{formatCurrency(arv)}</span>
                                                    </div>
                                                </div>
                                                <div className="vd-calc-raw">
                                                    <span className="vd-calc-raw-label">Full formula:</span>
                                                    <code className="vd-calc-raw-code">{arv_calculation}</code>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* Adjustments Section - Toggleable */}
            {adjustments && adjustments.length > 0 && (
                <div className="vd-section">
                    <div
                        className="vd-section-header vd-toggle-header"
                        onClick={() => setShowAdjustments(!showAdjustments)}
                    >
                        <span>
                            <span className="vd-section-icon">📐</span>
                            Adjustments Applied ({adjustments.length})
                        </span>
                        <span className={`vd-toggle-arrow ${showAdjustments ? 'open' : ''}`}>▼</span>
                    </div>
                    {showAdjustments && (
                        <div className="vd-adjustments-list">
                            {adjustments.map((adj, idx) => (
                                <div key={idx} className="vd-adjustment-item">
                                    <span className="vd-adjustment-factor">{adj.factor}</span>
                                    <span className={`vd-adjustment-pct ${adj.amount_pct >= 0 ? 'positive' : 'negative'}`}>
                                        {adj.amount_pct >= 0 ? '+' : ''}{adj.amount_pct}%
                                    </span>
                                    <span className="vd-adjustment-reason">{adj.reason}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Renovation Breakdown Section - Toggleable */}
            {renovation_breakdown && (
                <div className="vd-section">
                    <div
                        className="vd-section-header vd-toggle-header"
                        onClick={() => setShowRenovation(!showRenovation)}
                    >
                        <span>
                            <span className="vd-section-icon">🔧</span>
                            Renovation Cost Breakdown
                        </span>
                        <span className={`vd-toggle-arrow ${showRenovation ? 'open' : ''}`}>▼</span>
                    </div>
                    {showRenovation && (
                        <div className="vd-reno-grid">
                            {/* Base cost */}
                            {renovation_breakdown.base_cost > 0 && (
                                <div className="vd-reno-item">
                                    <div className="vd-reno-label">
                                        <span className="vd-reno-name">Base Cost</span>
                                        <span className="vd-reno-trigger">Condition-based estimate</span>
                                    </div>
                                    <span className="vd-reno-cost">{formatCurrency(renovation_breakdown.base_cost)}</span>
                                </div>
                            )}

                            {/* Item costs */}
                            {renovation_breakdown.item_costs?.map((item, idx) => (
                                <div key={idx} className="vd-reno-item">
                                    <div className="vd-reno-label">
                                        <span className="vd-reno-name">{item.item}</span>
                                        <span className="vd-reno-trigger">{item.trigger}</span>
                                    </div>
                                    <span className="vd-reno-cost">{formatCurrency(item.cost)}</span>
                                </div>
                            ))}

                            {/* Contingency */}
                            {renovation_breakdown.contingency_pct > 0 && (
                                <div className="vd-reno-item">
                                    <div className="vd-reno-label">
                                        <span className="vd-reno-name">Contingency</span>
                                        <span className="vd-reno-trigger">Risk buffer for older homes</span>
                                    </div>
                                    <span className="vd-reno-cost">+{renovation_breakdown.contingency_pct}%</span>
                                </div>
                            )}

                            {/* Total */}
                            {renovation_breakdown.total > 0 && (
                                <div className="vd-reno-item total">
                                    <div className="vd-reno-label">
                                        <span className="vd-reno-name">Total Renovation</span>
                                    </div>
                                    <span className="vd-reno-cost">{formatCurrency(renovation_breakdown.total)}</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

        </div>
    );
};

export default ValuationDisplay;
